import { useState, useEffect, useCallback } from "react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { KCard } from "@/components/KCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Tag,
  Plus,
  Search,
  Loader2,
  Edit2,
  Trash2,
  BarChart2,
  Eye,
  Copy,
  AlertCircle,
  CheckCircle2,
  Clock,
  XCircle,
  Sparkles,
  TrendingUp,
  Users,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { toast } from "sonner";
import {
  getCoupons,
  createCoupon,
  updateCoupon,
  deleteCoupon,
  getCouponAnalytics,
  type Coupon,
  type CouponTargetType,
  type DiscountType,
  type CouponAnalytics,
  type CouponStatus,
} from "@/api/lms/coupons";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getCouponStatus(coupon: Coupon): "active" | "expired" | "scheduled" | "disabled" {
  if (!coupon.isActive) return "disabled";
  const now = new Date();
  if (new Date(coupon.startDate) > now) return "scheduled";
  if (new Date(coupon.endDate) < now) return "expired";
  return "active";
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { label: string; icon: React.ElementType; cls: string }> = {
    active: { label: "Active", icon: CheckCircle2, cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    expired: { label: "Expired", icon: XCircle, cls: "bg-rose-50 text-rose-700 border-rose-200" },
    scheduled: { label: "Scheduled", icon: Clock, cls: "bg-blue-50 text-blue-700 border-blue-200" },
    disabled: { label: "Disabled", icon: AlertCircle, cls: "bg-gray-50 text-gray-600 border-gray-200" },
  };
  const s = map[status] ?? map.disabled;
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${s.cls}`}>
      <Icon size={10} />
      {s.label}
    </span>
  );
}

function TargetBadge({ type }: { type: CouponTargetType }) {
  const map: Record<CouponTargetType, string> = {
    COURSE: "bg-violet-50 text-violet-700",
    MOCK_TEST: "bg-amber-50 text-amber-700",
    ALL_COURSES: "bg-purple-50 text-purple-700",
    ALL_MOCK_TESTS: "bg-orange-50 text-orange-700",
    GLOBAL: "bg-primary/10 text-primary",
  };
  const labels: Record<CouponTargetType, string> = {
    COURSE: "Course",
    MOCK_TEST: "Mock Test",
    ALL_COURSES: "All Courses",
    ALL_MOCK_TESTS: "All Mock Tests",
    GLOBAL: "Global",
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${map[type]}`}>
      {labels[type]}
    </span>
  );
}

// ─── Coupon Form Modal ────────────────────────────────────────────────────────

interface CouponFormData {
  code: string;
  name: string;
  description: string;
  discountType: DiscountType;
  discountValue: string;
  targetType: CouponTargetType;
  targetId: string;
  startDate: string;
  endDate: string;
  usageLimit: string;
  perUserLimit: string;
  isActive: boolean;
  isAutoPromotion: boolean;
  minimumPurchaseAmount: string;
  isFirstPurchaseOnly: boolean;
}

const emptyForm: CouponFormData = {
  code: "",
  name: "",
  description: "",
  discountType: "PERCENTAGE",
  discountValue: "",
  targetType: "GLOBAL",
  targetId: "",
  startDate: "",
  endDate: "",
  usageLimit: "",
  perUserLimit: "1",
  isActive: true,
  isAutoPromotion: false,
  minimumPurchaseAmount: "",
  isFirstPurchaseOnly: false,
};

function toISODateInput(d: string) {
  try { return new Date(d).toISOString().slice(0, 16); } catch { return ""; }
}

function CouponFormModal({
  open,
  editing,
  onClose,
  onSaved,
  isAdmin,
}: {
  open: boolean;
  editing: Coupon | null;
  onClose: () => void;
  onSaved: () => void;
  isAdmin: boolean;
}) {
  const [form, setForm] = useState<CouponFormData>(emptyForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm({
        code: editing.code ?? "",
        name: editing.name,
        description: editing.description ?? "",
        discountType: editing.discountType,
        discountValue: editing.discountValue,
        targetType: editing.targetType,
        targetId: editing.targetId ?? "",
        startDate: toISODateInput(editing.startDate),
        endDate: toISODateInput(editing.endDate),
        usageLimit: editing.usageLimit !== null ? String(editing.usageLimit) : "",
        perUserLimit: String(editing.perUserLimit),
        isActive: editing.isActive,
        isAutoPromotion: editing.isAutoPromotion,
        minimumPurchaseAmount: editing.minimumPurchaseAmount !== null && editing.minimumPurchaseAmount !== undefined ? String(editing.minimumPurchaseAmount) : "",
        isFirstPurchaseOnly: editing.isFirstPurchaseOnly ?? false,
      });
    } else {
      // Default startDate = today, endDate = +30 days
      const now = new Date();
      const end = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
      setForm({
        ...emptyForm,
        startDate: now.toISOString().slice(0, 16),
        endDate: end.toISOString().slice(0, 16),
      });
    }
  }, [open, editing]);

  const set = (key: keyof CouponFormData) => (val: string | boolean) =>
    setForm((f) => ({ ...f, [key]: val }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.discountValue || !form.startDate || !form.endDate) {
      toast.error("Please fill all required fields");
      return;
    }
    setSaving(true);
    try {
      const dto: any = {
        code: form.code.trim() || undefined,
        name: form.name,
        description: form.description || undefined,
        discountType: form.discountType,
        discountValue: Number(form.discountValue),
        targetType: form.targetType,
        targetId: form.targetId.trim() || undefined,
        startDate: new Date(form.startDate).toISOString(),
        endDate: new Date(form.endDate).toISOString(),
        usageLimit: form.usageLimit ? Number(form.usageLimit) : undefined,
        perUserLimit: Number(form.perUserLimit) || 1,
        isActive: form.isActive,
        isAutoPromotion: form.isAutoPromotion,
      };

      if (form.minimumPurchaseAmount) {
        dto.minimumPurchaseAmount = Number(form.minimumPurchaseAmount);
      }
      if (form.isFirstPurchaseOnly) {
        dto.isFirstPurchaseOnly = true;
      }

      if (editing) {
        await updateCoupon(editing.id, dto);
        toast.success("Coupon updated");
      } else {
        await createCoupon(dto);
        toast.success("Coupon created");
      }
      onSaved();
      onClose();
    } catch (err: any) {
      const msg = err?.body?.message ?? err?.message ?? "Failed to save coupon";
      toast.error(typeof msg === "string" ? msg : "Failed to save coupon");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const needsTargetId = form.targetType === "COURSE" || form.targetType === "MOCK_TEST";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-background rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-border z-10">
        <div className="sticky top-0 bg-background border-b border-border px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <Tag size={16} className="text-primary" />
            </div>
            <h2 className="text-base font-bold text-foreground">
              {editing ? "Edit Coupon" : "Create Coupon"}
            </h2>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
            <XCircle size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Basic Info */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                Coupon Code
              </label>
              <Input
                placeholder="e.g. SAVE10 (leave empty for auto-promo)"
                value={form.code}
                onChange={(e) => set("code")(e.target.value.toUpperCase())}
                className="font-mono uppercase"
                disabled={form.isAutoPromotion}
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                Coupon Name <span className="text-destructive">*</span>
              </label>
              <Input
                placeholder="e.g. Summer Sale 10%"
                value={form.name}
                onChange={(e) => set("name")(e.target.value)}
                required
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Description</label>
            <textarea
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring min-h-[60px] resize-none"
              placeholder="Optional description for internal reference"
              value={form.description}
              onChange={(e) => set("description")(e.target.value)}
            />
          </div>

          {/* Discount */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                Discount Type <span className="text-destructive">*</span>
              </label>
              <select
                className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                value={form.discountType}
                onChange={(e) => set("discountType")(e.target.value)}
              >
                <option value="PERCENTAGE">Percentage (%)</option>
                <option value="FIXED">Fixed Amount (TK)</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                Discount Value <span className="text-destructive">*</span>
              </label>
              <div className="relative">
                <Input
                  type="number"
                  min="0"
                  max={form.discountType === "PERCENTAGE" ? "100" : undefined}
                  step="0.01"
                  placeholder={form.discountType === "PERCENTAGE" ? "e.g. 10" : "e.g. 100"}
                  value={form.discountValue}
                  onChange={(e) => set("discountValue")(e.target.value)}
                  required
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                  {form.discountType === "PERCENTAGE" ? "%" : "TK"}
                </span>
              </div>
            </div>
          </div>

          {/* Target */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                Target Type <span className="text-destructive">*</span>
              </label>
              <select
                className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                value={form.targetType}
                onChange={(e) => set("targetType")(e.target.value)}
              >
                <option value="COURSE">Specific Course</option>
                <option value="MOCK_TEST">Specific Mock Test</option>
                {isAdmin && (
                  <>
                    <option value="GLOBAL">Global (All courses + mock tests)</option>
                    <option value="ALL_COURSES">All Courses</option>
                    <option value="ALL_MOCK_TESTS">All Mock Tests</option>
                  </>
                )}
              </select>
            </div>
            {needsTargetId && (
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  {form.targetType === "COURSE" ? "Course ID" : "Mock Test ID"}{" "}
                  <span className="text-destructive">*</span>
                </label>
                <Input
                  placeholder="UUID of the target item"
                  value={form.targetId}
                  onChange={(e) => set("targetId")(e.target.value)}
                  className="font-mono text-xs"
                  required={needsTargetId}
                />
              </div>
            )}
          </div>

          {/* Validity */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                Start Date <span className="text-destructive">*</span>
              </label>
              <Input
                type="datetime-local"
                value={form.startDate}
                onChange={(e) => set("startDate")(e.target.value)}
                required
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                End Date <span className="text-destructive">*</span>
              </label>
              <Input
                type="datetime-local"
                value={form.endDate}
                onChange={(e) => set("endDate")(e.target.value)}
                required
              />
            </div>
          </div>

          {/* Limits */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                Total Usage Limit
              </label>
              <Input
                type="number"
                min="1"
                placeholder="Leave empty for unlimited"
                value={form.usageLimit}
                onChange={(e) => set("usageLimit")(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                Per User Limit
              </label>
              <Input
                type="number"
                min="1"
                max="100"
                value={form.perUserLimit}
                onChange={(e) => set("perUserLimit")(e.target.value)}
              />
            </div>
          </div>

          {/* Toggles */}
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => set("isActive")(e.target.checked)}
                className="accent-primary w-4 h-4"
              />
              <span className="text-sm font-medium text-foreground">Active</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={form.isAutoPromotion}
                onChange={(e) => {
                  set("isAutoPromotion")(e.target.checked);
                  if (e.target.checked) set("code")("");
                }}
                className="accent-primary w-4 h-4"
              />
              <span className="text-sm font-medium text-foreground">Auto-promotion (no code needed)</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={form.isFirstPurchaseOnly}
                onChange={(e) => set("isFirstPurchaseOnly")(e.target.checked)}
                className="accent-primary w-4 h-4"
              />
              <span className="text-sm font-medium text-foreground">First Purchase Only</span>
            </label>
          </div>

          {/* Min Purchase */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                Minimum Purchase Amount (TK)
              </label>
              <Input
                type="number"
                min="0"
                step="0.01"
                placeholder="Leave empty for no minimum"
                value={form.minimumPurchaseAmount}
                onChange={(e) => set("minimumPurchaseAmount")(e.target.value)}
              />
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button type="submit" disabled={saving} className="flex-1">
              {saving ? <Loader2 size={14} className="animate-spin mr-1" /> : null}
              {editing ? "Save Changes" : "Create Coupon"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function AdminCouponsPage() {
  const { user } = useAuth();
  const isAdmin = user?.backendRole === "SYSTEM_ADMIN";

  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState<CouponAnalytics | null>(null);

  const [statusFilter, setStatusFilter] = useState<CouponStatus | "all">("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Coupon | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);

  const pageSize = 20;

  const fetchCoupons = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getCoupons({ status: statusFilter, search, page, limit: pageSize });
      setCoupons(res.items);
      setTotal(res.total);
    } catch {
      toast.error("Failed to load coupons");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, search, page]);

  const fetchAnalytics = useCallback(async () => {
    try {
      const data = await getCouponAnalytics();
      setAnalytics(data);
    } catch {
      // silently ignore
    }
  }, []);

  useEffect(() => {
    fetchCoupons();
  }, [fetchCoupons]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const handleDelete = async (id: string) => {
    if (!confirm("Delete this coupon? This cannot be undone.")) return;
    setDeleting(id);
    try {
      await deleteCoupon(id);
      toast.success("Coupon deleted");
      fetchCoupons();
      fetchAnalytics();
    } catch (err: any) {
      toast.error(err?.message ?? "Failed to delete");
    } finally {
      setDeleting(null);
    }
  };

  const handleCopy = (code: string) => {
    navigator.clipboard.writeText(code).then(() => toast.success("Code copied!"));
  };

  const totalPages = Math.ceil(total / pageSize);

  const statsCards = [
    {
      label: "Total Coupons",
      value: analytics?.totalCoupons ?? 0,
      sub: `${analytics?.activeCoupons ?? 0} active`,
      icon: Tag,
      color: "bg-primary/10 text-primary",
    },
    {
      label: "Total Uses",
      value: analytics?.totalUses ?? 0,
      sub: "All time redemptions",
      icon: Users,
      color: "bg-blue-50 text-blue-600",
    },
    {
      label: "Total Savings Given",
      value: `৳${(analytics?.totalSavings ?? 0).toLocaleString()}`,
      sub: "Discounts applied",
      icon: TrendingUp,
      color: "bg-emerald-50 text-emerald-600",
    },
    {
      label: "Revenue Generated",
      value: `৳${(analytics?.totalRevenue ?? 0).toLocaleString()}`,
      sub: "After discounts",
      icon: BarChart2,
      color: "bg-violet-50 text-violet-600",
    },
  ];

  return (
    <DashboardLayout>
      <div className="dash-page">
        {/* Header */}
        <div className="dash-page-header">
          <div>
            <h1 className="dash-page-title flex items-center gap-2">
              <Tag size={20} className="text-primary" />
              Coupons & Promotions
            </h1>
            <p className="dash-page-subtitle">
              Manage discount codes and automatic promotional campaigns
            </p>
          </div>
          <Button size="sm" onClick={() => { setEditing(null); setModalOpen(true); }}>
            <Plus size={14} className="mr-1.5" />
            Create Coupon
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {statsCards.map((s, i) => (
            <KCard key={i} className="dash-stat-card">
              <div className="flex items-start justify-between">
                <div className={`dash-stat-icon ${s.color}`}>
                  <s.icon size={18} />
                </div>
                {analytics?.expiringThisWeek && analytics.expiringThisWeek > 0 && s.label === "Total Coupons" && (
                  <span className="text-[10px] bg-amber-50 text-amber-700 border border-amber-200 px-1.5 py-0.5 rounded-full font-semibold">
                    {analytics.expiringThisWeek} expiring
                  </span>
                )}
              </div>
              <div>
                <p className="dash-stat-value">{s.value}</p>
                <p className="dash-stat-label mt-1">{s.label}</p>
              </div>
              <p className="text-[11px] text-muted-foreground/70">{s.sub}</p>
            </KCard>
          ))}
        </div>

        {/* Filter bar */}
        <div className="flex flex-wrap gap-3 mb-5">
          <div className="flex gap-1 bg-muted rounded-lg p-1">
            {(["all", "active", "expired", "scheduled", "disabled"] as const).map((s) => (
              <button
                key={s}
                onClick={() => { setStatusFilter(s); setPage(1); }}
                className={`px-3 py-1 rounded-md text-xs font-semibold capitalize transition-all ${
                  statusFilter === s
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
          <div className="relative flex-1 min-w-[180px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search coupons..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="pl-8 h-10 text-sm"
            />
          </div>
        </div>

        {/* Table */}
        <KCard className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full dash-table">
              <thead>
                <tr>
                  <th>Code / Name</th>
                  <th>Discount</th>
                  <th>Target</th>
                  <th>Validity</th>
                  <th>Usage</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center text-muted-foreground">
                      <Loader2 className="animate-spin inline mr-2" size={16} />
                      Loading coupons…
                    </td>
                  </tr>
                ) : coupons.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="p-4">
                      <div className="dash-empty-state">
                        <Tag size={32} className="text-muted-foreground/30 mb-2" />
                        <p className="text-sm font-medium mb-4">No coupons found</p>
                        <Button size="sm" variant="outline" onClick={() => { setEditing(null); setModalOpen(true); }}>
                          <Plus size={13} className="mr-1" /> Create your first coupon
                        </Button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  coupons.map((c) => {
                    const status = getCouponStatus(c);
                    return (
                      <tr key={c.id}>
                        <td>
                          <div className="space-y-0.5">
                            {c.code ? (
                              <div className="flex items-center gap-1.5">
                                <span className="font-mono text-xs font-bold text-foreground bg-muted px-2 py-0.5 rounded">
                                  {c.code}
                                </span>
                                <button
                                  onClick={() => handleCopy(c.code!)}
                                  className="text-muted-foreground hover:text-foreground transition-colors"
                                  title="Copy code"
                                >
                                  <Copy size={11} />
                                </button>
                              </div>
                            ) : (
                              <div className="flex items-center gap-1">
                                <Sparkles size={12} className="text-amber-500" />
                                <span className="text-[11px] text-amber-600 font-semibold">Auto-promo</span>
                              </div>
                            )}
                            <p className="text-xs text-muted-foreground truncate max-w-[160px]">{c.name}</p>
                          </div>
                        </td>
                        <td>
                          <span className="text-sm font-bold text-foreground">
                            {c.discountType === "PERCENTAGE"
                              ? `${parseFloat(c.discountValue)}%`
                              : `৳${parseFloat(c.discountValue).toLocaleString()}`}
                          </span>
                          <p className="text-[11px] text-muted-foreground">
                            {c.discountType === "PERCENTAGE" ? "Percentage" : "Fixed"}
                          </p>
                        </td>
                        <td>
                          <TargetBadge type={c.targetType} />
                        </td>
                        <td>
                          <p className="text-xs text-foreground">
                            {new Date(c.startDate).toLocaleDateString()}
                          </p>
                          <p className="text-[11px] text-muted-foreground">
                            → {new Date(c.endDate).toLocaleDateString()}
                          </p>
                        </td>
                        <td>
                          <span className="text-sm font-semibold text-foreground">{c.totalUsed}</span>
                          <span className="text-xs text-muted-foreground">
                            {c.usageLimit !== null ? ` / ${c.usageLimit}` : " / ∞"}
                          </span>
                        </td>
                        <td>
                          <StatusBadge status={status} />
                        </td>
                        <td>
                          <div className="flex items-center gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 px-2"
                              asChild
                            >
                              <Link to={`${isAdmin ? "/admin" : "/teacher"}/coupons/${c.id}`}>
                                <Eye size={13} />
                              </Link>
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 px-2"
                              onClick={() => { setEditing(c); setModalOpen(true); }}
                            >
                              <Edit2 size={13} />
                            </Button>
                            {isAdmin && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 px-2 text-destructive hover:text-destructive"
                                disabled={deleting === c.id}
                                onClick={() => handleDelete(c.id)}
                              >
                                {deleting === c.id
                                  ? <Loader2 size={13} className="animate-spin" />
                                  : <Trash2 size={13} />
                                }
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-border">
              <p className="text-xs text-muted-foreground">
                {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of {total}
              </p>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
                  Previous
                </Button>
                <Button size="sm" variant="outline" disabled={page === totalPages} onClick={() => setPage((p) => p + 1)}>
                  Next
                </Button>
              </div>
            </div>
          )}
        </KCard>
      </div>

      {/* Create / Edit Modal */}
      <CouponFormModal
        open={modalOpen}
        editing={editing}
        isAdmin={isAdmin}
        onClose={() => { setModalOpen(false); setEditing(null); }}
        onSaved={() => {
          fetchCoupons();
          fetchAnalytics();
        }}
      />
    </DashboardLayout>
  );
}
